import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useHireMasterLabels } from '@/hooks/useHireMasterLabels';
import pmvHireService, { type PmvHireAgreement } from '@/services/pmv-hire.service';
import {
  HIRE_LIFECYCLE_ACTION_LABEL,
  HIRE_STATUS_COLOR,
  type HireLifecycleAction,
  hireLifecycleActionsFor,
  hireTableCellEllipsis,
  hireTableLinkBtnStyle,
  resolveLabel,
} from '@/utils/pmvHireUi';

const PmvHireOnOffHirePage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const statusFilter = params.get('status') || undefined;
  const focusAgreement = params.get('agreement') || undefined;
  const [actionOpen, setActionOpen] = useState<{
    agreement: PmvHireAgreement;
    action: HireLifecycleAction;
  } | null>(null);
  const [form] = Form.useForm();

  const { supplierLabelById, equipmentLabelById, projectLabelById } = useHireMasterLabels();

  const listQ = useQuery({
    queryKey: ['pmv-hire-on-off', statusFilter],
    queryFn: () =>
      pmvHireService.listAgreements({
        status: statusFilter,
        limit: 200,
      }),
  });

  const rows = useMemo(() => {
    const items = listQ.data?.items ?? [];
    if (!focusAgreement) return items;
    return items
      .filter((a) => a.id === focusAgreement)
      .concat(items.filter((a) => a.id !== focusAgreement));
  }, [listQ.data, focusAgreement]);

  const focused = useMemo(
    () => (focusAgreement ? rows.find((a) => a.id === focusAgreement) : undefined),
    [focusAgreement, rows]
  );

  const mut = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (!actionOpen) throw new Error('No action');
      const { agreement, action } = actionOpen;
      if (action === 'on-hire') return pmvHireService.onHire(agreement.id, body);
      if (action === 'off-hire-request') return pmvHireService.offHireRequest(agreement.id, body);
      if (action === 'off-hire-effective') return pmvHireService.offHireEffective(agreement.id, body);
      return pmvHireService.demobilize(agreement.id, body);
    },
    onSuccess: () => {
      message.success('Lifecycle updated');
      setActionOpen(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['pmv-hire-on-off'] });
      qc.invalidateQueries({ queryKey: ['pmv-hire-register'] });
      qc.invalidateQueries({ queryKey: ['pmv-hire-control-tower'] });
    },
    onError: (e: Error) => message.error(e.message || 'Lifecycle failed'),
  });

  const openAction = (agreement: PmvHireAgreement, action: HireLifecycleAction) => {
    setActionOpen({ agreement, action });
    form.setFieldsValue({
      effective_at: dayjs(),
      notes: undefined,
    });
  };

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="On-Hire / Off-Hire"
        subtitle="Commercial hire dates are charging authority — GPS / physical pickup is evidence only"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/hire/control')}>Hire Control</Button>
            <Button onClick={() => navigate('/asset/pmv/hire')}>On-Hire register</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/agreements')}>Agreements</Button>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Filter status"
            style={{ width: 200 }}
            value={statusFilter}
            onChange={(v) => {
              const next = new URLSearchParams(params);
              if (v) next.set('status', v);
              else next.delete('status');
              navigate({ search: next.toString() });
            }}
            options={[
              'ON_HIRE',
              'OFF_HIRE_REQUESTED',
              'OFF_HIRE_EFFECTIVE',
              'MOBILIZING',
              'DEMOBILIZING',
              'CONFIRMED',
              'PLANNED',
            ].map((v) => ({ value: v, label: v }))}
          />
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Charging stops at contractual OFF_HIRE_EFFECTIVE, not physical demobilization."
      />

      {focused ? (
        <Card size="small" title="Focused agreement" style={{ marginBottom: 12 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
            <Descriptions.Item label="Agreement">
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => navigate(`/asset/pmv/hire/agreements/${focused.id}`)}
              >
                {focused.agreement_number}
              </Button>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={HIRE_STATUS_COLOR[focused.commercial_status] || 'default'}>
                {focused.commercial_status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Equipment">
              {resolveLabel(equipmentLabelById, focused.pmv_profile_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Supplier">
              {resolveLabel(supplierLabelById, focused.supplier_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Project">
              {resolveLabel(projectLabelById, focused.project_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Currency">{focused.currency}</Descriptions.Item>
          </Descriptions>
        </Card>
      ) : null}

      <Table
        rowKey="id"
        size="small"
        loading={listQ.isLoading}
        dataSource={rows}
        scroll={{ x: 1520 }}
        tableLayout="fixed"
        pagination={{ pageSize: 25 }}
        columns={[
          {
            title: 'Agreement',
            dataIndex: 'agreement_number',
            width: 180,
            ellipsis: true,
            render: (v: string, r: PmvHireAgreement) => (
              <div style={hireTableCellEllipsis}>
                <Button
                  type="link"
                  style={hireTableLinkBtnStyle}
                  onClick={() => navigate(`/asset/pmv/hire/agreements/${r.id}`)}
                >
                  {v}
                </Button>
              </div>
            ),
          },
          {
            title: 'Equipment',
            dataIndex: 'pmv_profile_id',
            width: 220,
            ellipsis: true,
            render: (id: string) => (
              <div style={hireTableCellEllipsis}>{resolveLabel(equipmentLabelById, id)}</div>
            ),
          },
          {
            title: 'Supplier',
            dataIndex: 'supplier_id',
            width: 200,
            ellipsis: true,
            render: (id: string) => (
              <div style={hireTableCellEllipsis}>{resolveLabel(supplierLabelById, id)}</div>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'commercial_status',
            width: 170,
            render: (s: string) => <Tag color={HIRE_STATUS_COLOR[s] || 'default'}>{s}</Tag>,
          },
          {
            title: 'On-hire at',
            dataIndex: 'commercial_on_hire_at',
            width: 170,
            ellipsis: true,
            render: (v) => (v ? String(v).slice(0, 19) : '—'),
          },
          {
            title: 'Off-hire requested',
            dataIndex: 'commercial_off_hire_requested_at',
            width: 170,
            ellipsis: true,
            render: (v) => (v ? String(v).slice(0, 19) : '—'),
          },
          {
            title: 'Off-hire effective',
            dataIndex: 'commercial_off_hire_effective_at',
            width: 170,
            ellipsis: true,
            render: (v) => (v ? String(v).slice(0, 19) : '—'),
          },
          {
            title: 'Actions',
            width: 200,
            fixed: 'right',
            render: (_: unknown, r: PmvHireAgreement) => {
              const allowed = hireLifecycleActionsFor(r.commercial_status);
              const primary = allowed[0];
              const items: MenuProps['items'] = (
                [
                  'on-hire',
                  'off-hire-request',
                  'off-hire-effective',
                  'demobilize',
                ] as HireLifecycleAction[]
              ).map((action) => ({
                key: action,
                label: HIRE_LIFECYCLE_ACTION_LABEL[action],
                disabled: !allowed.includes(action),
                onClick: () => openAction(r, action),
              }));
              return (
                <Space size={4} wrap={false}>
                  {primary ? (
                    <Button size="small" type="primary" onClick={() => openAction(r, primary)}>
                      {primary === 'on-hire'
                        ? 'On-hire'
                        : primary === 'off-hire-request'
                          ? 'Off-hire req'
                          : primary === 'off-hire-effective'
                            ? 'Off-hire eff'
                            : 'Demobilize'}
                    </Button>
                  ) : (
                    <Tag>No action</Tag>
                  )}
                  <Dropdown menu={{ items }} trigger={['click']}>
                    <Button size="small">More</Button>
                  </Dropdown>
                </Space>
              );
            },
          },
        ]}
      />

      <Modal
        title={
          actionOpen
            ? `${HIRE_LIFECYCLE_ACTION_LABEL[actionOpen.action]} — ${actionOpen.agreement.agreement_number}`
            : 'Lifecycle'
        }
        open={!!actionOpen}
        onCancel={() => setActionOpen(null)}
        onOk={() => form.submit()}
        confirmLoading={mut.isPending}
        okText="Confirm"
        width={560}
      >
        {actionOpen ? (
          <>
            <Alert
              style={{ marginBottom: 12 }}
              type={actionOpen.action === 'off-hire-effective' ? 'warning' : 'info'}
              showIcon
              message={
                actionOpen.action === 'off-hire-effective'
                  ? 'Off-hire effective stops commercial charging. Physical demobilization is a separate step.'
                  : actionOpen.action === 'demobilize'
                    ? 'Physical demobilization does not change the commercial off-hire effective date.'
                    : 'Confirm the commercial effective timestamp for this lifecycle step.'
              }
            />
            <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Equipment">
                {resolveLabel(equipmentLabelById, actionOpen.agreement.pmv_profile_id)}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier">
                {resolveLabel(supplierLabelById, actionOpen.agreement.supplier_id)}
              </Descriptions.Item>
              <Descriptions.Item label="Current status">
                <Tag color={HIRE_STATUS_COLOR[actionOpen.agreement.commercial_status] || 'default'}>
                  {actionOpen.agreement.commercial_status}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : null}
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) =>
            mut.mutate({
              ...v,
              effective_at: v.effective_at ? dayjs(v.effective_at).toISOString() : undefined,
              notes: v.notes,
            })
          }
        >
          <Form.Item name="effective_at" label="Effective at" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional commercial / site notes" />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default PmvHireOnOffHirePage;
